// oxlint-disable jsx-no-new-object-as-prop

import { Link } from "@tanstack/react-router";
import { animate, m, stagger } from "motion/react";
import { useCallback, useMemo, useRef } from "react";

import { useGlobal } from "../../lib/global-context";
import type { Pillar as PillarType, VoidComponent } from "../../lib/types";
import { imageUrlBuilder, urlFromSanityId } from "../../lib/utils";
import { Category } from "./category";
import { introVariants, transitionAnimate, transitionExit } from "./const";

const Pillar: VoidComponent<PillarType> = (props) => {
	const { aboutGoToTopicText } = useGlobal();
	const pillarSlug = props.slug.current;
	const params = useMemo(() => ({ pillar: pillarSlug }), [pillarSlug]);

	const videoUrl = useMemo(() => urlFromSanityId(props.video.asset._ref), [props.video.asset._ref]);

	const videoRef = useRef<HTMLVideoElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);

	const handleVideoLoad = useCallback(() => {
		if (!videoRef.current || !imageRef.current) {
			return;
		}

		animate(videoRef.current, { opacity: 1 }, { duration: 0.3 });
		animate(imageRef.current, { opacity: 0 }, { duration: 0.3 });
	}, []);

	const handleImageLoad = useCallback(() => {
		if (
			!videoRef.current ||
			!imageRef.current ||
			videoRef.current.readyState < HTMLMediaElement.HAVE_FUTURE_DATA
		) {
			return;
		}

		animate(videoRef.current, { opacity: 1 }, { duration: 0.3 });
		animate(imageRef.current, { opacity: 0 }, { duration: 0.3 });
	}, []);

	return (
		<li className="gap-8 desktop:grid desktop:grid-cols-12 *:desktop:col-span-6">
			<m.div
				className="mt-4 border-t border-stroke-muted pt-4 desktop:grid desktop:grid-cols-subgrid"
				variants={{
					initial: { opacity: 0, y: "1rem" },
					animate: {
						opacity: 1,
						y: "0rem",
						transition: {
							...transitionAnimate,
							delayChildren: stagger(0.05, { startDelay: 0.2 }),
							delay: 0.2,
						},
					},
					exit: { opacity: 0, y: "1rem", transition: transitionExit },
				}}
				initial="initial"
				animate="animate"
				exit="exit"
			>
				<Link
					to="/$pillar"
					params={params}
					className="group relative block h-fit p-4 desktop:sticky desktop:top-8 desktop:col-span-4 desktop:self-start"
				>
					<m.div className="absolute inset-0 -z-1 bg-white" variants={introVariants} />

					<div className="relative z-1 mb-4 aspect-square w-full overflow-hidden">
						{videoUrl && (
							<m.video
								ref={videoRef}
								src={videoUrl}
								className="absolute -inset-12 size-[calc(100%+6rem)] max-w-none opacity-0 mix-blend-multiply"
								variants={introVariants}
								autoPlay
								muted
								loop
								playsInline
								preload="auto"
								controls={false}
								disableRemotePlayback
								disablePictureInPicture
								onCanPlay={handleVideoLoad}
							>
								<track kind="captions" />
							</m.video>
						)}

						<m.img
							ref={imageRef}
							src={imageUrlBuilder(props.thumbnail)
								.width(620)
								.auto("format")
								.dpr(Math.min(window.devicePixelRatio, 2))
								.url()}
							alt="Island placeholder"
							className="absolute -inset-12 size-[calc(100%+6rem)] max-w-none"
							variants={introVariants}
							onLoad={handleImageLoad}
						/>
					</div>

					<div className="relative group-hover:opacity-70">
						<m.span
							className="flex items-start gap-2 border-t border-stroke-muted"
							variants={introVariants}
						>
							<h3 className="my-3 text-xl text-foreground">{props.title}</h3>
						</m.span>

						<m.p className="font-serif text-xs text-foreground" variants={introVariants}>
							{props.description}
						</m.p>

						<m.p
							className="mt-8 flex w-fit items-center gap-2 text-xs text-foreground underline"
							variants={introVariants}
						>
							{aboutGoToTopicText}
						</m.p>
					</div>
				</Link>
			</m.div>

			<m.ul
				className="mt-4 flex flex-col gap-4 border-stroke-accent"
				variants={{
					initial: { opacity: 0, y: "1rem" },
					animate: {
						opacity: 1,
						y: "0rem",
						transition: { ...transitionAnimate, delayChildren: stagger(0.05, { startDelay: 0.2 }) },
					},
					exit: { opacity: 0, y: "1rem", transition: transitionExit },
				}}
				initial="initial"
				animate="animate"
				exit="exit"
			>
				{props.categories.map((category) => (
					<Category key={category._key} {...category} pillar={props.slug.current} />
				))}
			</m.ul>
		</li>
	);
};

export { Pillar };
